import React, { useState, useEffect } from "react";
import {
  submitChangeRequest,
  getPresignedUploadUrl,
  uploadFileToS3,
} from "../services/platformApi";
import "./EditProfileModal.css";

/**
 * EditProfileModal — employee edits allowed fields + optionally re-uploads
 * Aadhar / PAN / Photo. On submit it does NOT change data directly — it
 * sends a change request to the admin for approval.
 *
 * Place at: src/components/EditProfileModal.js
 * Props: { profile, onClose, onSubmitted }
 */

/* ── Inline SVG icons (pure inline vectors, no icon library) ── */
const Svg = {
  pencil: (
    <svg viewBox="0 0 24 24"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" /></svg>
  ),
  info: (
    <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" /></svg>
  ),
  mail: (
    <svg viewBox="0 0 24 24"><path d="M4 4h16v16H4z" /><path d="M4 8l8 5 8-5" /></svg>
  ),
  home: (
    <svg viewBox="0 0 24 24"><path d="M3 11l9-8 9 8" /><path d="M5 10v10h14V10" /></svg>
  ),
  user: (
    <svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 016-6h4a6 6 0 016 6v1" /></svg>
  ),
  upload: (
    <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><path d="M7 10l5 5 5-5M12 15V3" /></svg>
  ),
  card: (
    <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18" /></svg>
  ),
  idcard: (
    <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 9h4M7 13h6" /></svg>
  ),
  photo: (
    <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="M21 15l-5-5L5 21" /></svg>
  ),
  note: (
    <svg viewBox="0 0 24 24"><path d="M4 4h16v12H8l-4 4z" /></svg>
  ),
  check: (
    <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M9 12l2 2 4-4" /></svg>
  ),
  send: (
    <svg viewBox="0 0 24 24"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" /></svg>
  ),
  warn: (
    <svg viewBox="0 0 24 24"><path d="M10.3 3.9l-8 14A2 2 0 004 21h16a2 2 0 001.7-3.1l-8-14a2 2 0 00-3.4 0z" /><path d="M12 9v4M12 17h.01" /></svg>
  ),
};

const DOC_RULES = {
  aadhar: { key: "aadharDocKey", label: "Aadhar Card", accept: ".pdf,.jpg,.jpeg,.png", types: ["application/pdf", "image/jpeg", "image/png"], max: 5 * 1024 * 1024, maxLabel: "5MB", hint: "PDF/JPG/PNG · 5MB", icon: "card" },
  pan:    { key: "panDocKey",    label: "PAN Card",    accept: ".pdf,.jpg,.jpeg,.png", types: ["application/pdf", "image/jpeg", "image/png"], max: 5 * 1024 * 1024, maxLabel: "5MB", hint: "PDF/JPG/PNG · 5MB", icon: "idcard" },
  photo:  { key: "photoKey",     label: "Passport Photo", accept: ".jpg,.jpeg,.png",   types: ["image/jpeg", "image/png"], max: 2 * 1024 * 1024, maxLabel: "2MB", hint: "JPG/PNG · 2MB", icon: "photo" },
};

// Section icon map
const SECTION_ICON = {
  "Contact": "mail",
  "Current Address": "home",
  "Permanent Address": "home",
  "Emergency Contact": "user",
};

// The text fields to render (kept OUTSIDE the component so inputs never lose focus)
const TEXT_FIELDS = [
  { section: "Contact", items: [
    { k: "personalEmail", label: "Personal Email", type: "email" },
    { k: "phone", label: "Phone", maxLength: 10 },
  ]},
  { section: "Current Address", items: [
    { k: "currentAddress", label: "Address", full: true },
    { k: "currentCity", label: "City" },
    { k: "currentState", label: "State" },
    { k: "currentPincode", label: "Pincode", maxLength: 6 },
  ]},
  { section: "Permanent Address", items: [
    { k: "permanentAddress", label: "Address", full: true },
    { k: "permanentCity", label: "City" },
    { k: "permanentState", label: "State" },
    { k: "permanentPincode", label: "Pincode", maxLength: 6 },
  ]},
  { section: "Emergency Contact", items: [
    { k: "emergencyContactName", label: "Contact Name" },
    { k: "emergencyContactPhone", label: "Contact Phone", maxLength: 10 },
  ]},
];

function EditProfileModal({ profile, onClose, onSubmitted }) {
  const p = profile || {};
  const [form, setForm] = useState({
    personalEmail: p.personalEmail || "",
    phone: p.phone || "",
    bloodGroup: p.bloodGroup || "",
    currentAddress: p.currentAddress || "",
    currentCity: p.currentCity || "",
    currentState: p.currentState || "",
    currentPincode: p.currentPincode || "",
    permanentAddress: p.permanentAddress || "",
    permanentCity: p.permanentCity || "",
    permanentState: p.permanentState || "",
    permanentPincode: p.permanentPincode || "",
    emergencyContactName: p.emergencyContactName || "",
    emergencyContactPhone: p.emergencyContactPhone || "",
  });
  const [reason, setReason] = useState("");
  const [docs, setDocs] = useState({});
  const [docErr, setDocErr] = useState({});
  const [previews, setPreviews] = useState({}); // type -> objectURL (image preview)
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Clean up object URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      Object.values(previews).forEach((url) => url && URL.revokeObjectURL(url));
    };
  }, [previews]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickDoc = (type, file) => {
    const r = DOC_RULES[type];
    if (!file) return;
    if (!r.types.includes(file.type)) {
      setDocErr((e) => ({ ...e, [type]: `Allowed: ${r.accept}` }));
      return;
    }
    if (file.size > r.max) {
      setDocErr((e) => ({ ...e, [type]: `Max ${r.maxLabel}` }));
      return;
    }
    setDocErr((e) => ({ ...e, [type]: "" }));
    setDocs((d) => ({ ...d, [type]: file }));

    // Build an image preview for image files (revoke previous first)
    setPreviews((prev) => {
      if (prev[type]) URL.revokeObjectURL(prev[type]);
      const next = { ...prev };
      if (file.type.startsWith("image/")) {
        next[type] = URL.createObjectURL(file);
      } else {
        delete next[type];
      }
      return next;
    });
  };

  const changedFields = () => {
    const out = {};
    Object.keys(form).forEach((k) => {
      const orig = p[k] || "";
      if ((form[k] || "") !== orig) out[k] = form[k];
    });
    return out;
  };

  const handleSubmit = async () => {
    setError("");
    const changes = changedFields();
    const docTypes = Object.keys(docs);

    if (Object.keys(changes).length === 0 && docTypes.length === 0) {
      setError("Nothing changed. Edit a field or re-upload a document first.");
      return;
    }

    setSubmitting(true);
    try {
      const documents = {};
      for (const type of docTypes) {
        const file = docs[type];
        const { uploadUrl, s3Key } = await getPresignedUploadUrl(type, file.type, file.size);
        if (!uploadUrl || !s3Key) throw new Error(`Could not get upload URL for ${type}`);
        await uploadFileToS3(uploadUrl, file, file.type);
        documents[DOC_RULES[type].key] = s3Key;
      }
      await submitChangeRequest({ changes, documents, reason });
      onSubmitted && onSubmitted();
    } catch (err) {
      setError(err.message || "Could not submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="epm-overlay">
      <div className="epm-box" onClick={(e) => e.stopPropagation()}>
        {/* Gradient header */}
        <div className="epm-head">
          <div className="epm-head__l">
            <div className="epm-head__ic">{Svg.pencil}</div>
            <div>
              <h3>Edit Profile</h3>
              <p>Changes need admin approval</p>
            </div>
          </div>
          <button className="epm-close" onClick={onClose}>×</button>
        </div>

        <div className="epm-note">
          {Svg.info}
          <span>Your changes are sent to the admin for approval. They will not apply until an admin approves them.</span>
        </div>

        <div className="epm-body">
          {TEXT_FIELDS.map((group) => (
            <div key={group.section}>
              <h4 className="epm-section">
                <span className="epm-section__ic">{Svg[SECTION_ICON[group.section]] || Svg.mail}</span>
                {group.section}
              </h4>
              <div className="epm-grid">
                {group.items.map((f) => (
                  <div className={`epm-field ${f.full ? "epm-field--full" : ""}`} key={f.k}>
                    <label>{f.label}</label>
                    <input
                      type={f.type || "text"}
                      className="epm-input"
                      value={form[f.k]}
                      maxLength={f.maxLength}
                      onChange={(e) => set(f.k, e.target.value)}
                    />
                  </div>
                ))}
                {/* Blood group dropdown sits in the Contact section */}
                {group.section === "Contact" && (
                  <div className="epm-field">
                    <label>Blood Group</label>
                    <select className="epm-input" value={form.bloodGroup} onChange={(e) => set("bloodGroup", e.target.value)}>
                      <option value="">Select</option>
                      {["A+","A-","B+","B-","O+","O-","AB+","AB-"].map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
          ))}

          <h4 className="epm-section">
            <span className="epm-section__ic">{Svg.upload}</span>
            Re-upload Documents (optional)
          </h4>
          <div className="epm-docs">
            {Object.keys(DOC_RULES).map((type) => {
              const r = DOC_RULES[type];
              const file = docs[type];
              const prev = previews[type];
              return (
                <label key={type} className={`epm-doc ${file ? "epm-doc--filled" : ""}`}>
                  {prev ? (
                    <img className="epm-doc__prev" src={prev} alt={r.label} />
                  ) : (
                    <div className="epm-doc__ic">{Svg[r.icon]}</div>
                  )}
                  <div className="epm-doc__lbl">{r.label}</div>
                  {!file && <div className="epm-doc__hint">{r.hint}</div>}
                  {file && (
                    <span className="epm-doc__ok" title={file.name}>
                      {Svg.check} {file.name}
                    </span>
                  )}
                  {docErr[type] && <span className="epm-doc__err">{docErr[type]}</span>}
                  <input
                    type="file"
                    accept={r.accept}
                    onChange={(e) => e.target.files[0] && pickDoc(type, e.target.files[0])}
                  />
                </label>
              );
            })}
          </div>

          <h4 className="epm-section">
            <span className="epm-section__ic">{Svg.note}</span>
            Reason (optional)
          </h4>
          <textarea
            className="epm-input epm-textarea"
            rows={2}
            placeholder="Why are you making these changes?"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />

          {error && <div className="epm-error">{Svg.warn} {error}</div>}
        </div>

        <div className="epm-actions">
          <button className="epm-btn epm-btn--ghost" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="epm-btn epm-btn--primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Submitting..." : <>{Svg.send} Submit for Approval</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditProfileModal;
